import { TrashIcon } from "@xola/icons";
import { Alert, flash } from "@xola/ui-kit";
import React from "react";
import { useMutation } from "react-query";
import { RemoteCard, Traveler } from "types";
import { api } from "../../../api/api";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { useModal } from "../../../hooks/useVisibility";
import { CreditCardIcon } from "../../Common/CreditCardIcon";
import { InlineConfirmDeletePopover } from "../../Common/InlineConfirmDeletePopover";
import { NoTranslate } from "../../Common/NoTranslate";
import { PromptModal } from "../../Common/PromptModal";
import { SecondaryButton } from "../../Common/SecondaryButton";

interface ContactCardListProps {
    cards?: RemoteCard[];
    contact: Traveler;
}

export const ContactCardList = ({ cards, contact }: ContactCardListProps) => {
    return (
        <div className="pt-10">
            <h3 className="mb-5" data-testid="contact-card-list-header">
                Credit Cards
            </h3>

            <div className="flex flex-wrap gap-3">
                {cards?.length ? (
                    cards?.map((card) => <CardItem key={card.id} card={card} contact={contact} />)
                ) : (
                    <span>No cards found for this customer</span>
                )}
            </div>
        </div>
    );
};

interface CardItemProps {
    card: RemoteCard;
    contact: Traveler;
}

const CardItem = ({ card, contact }: CardItemProps) => {
    const { isVisible: isVisiblePrompt, toggle: togglePrompt } = useModal(false);
    const invalidate = useInvalidate();

    const { mutateAsync: forceDeleteCard } = useMutation(async () => {
        await api.delete(`/users/${contact.id}/remoteCards/${card.id}?force=true`);
        invalidate("users", contact.id);
        flash.success("Card deleted");
    });

    const { mutateAsync: deleteCard } = useMutation(async (id: ID) => {
        return api.delete(`/users/${contact.id}/remoteCards/${id}`);
    });

    const handleDeleteCard = async (cardId: string) => {
        try {
            if (cardId) {
                await deleteCard(cardId);
                invalidate("users", contact.id);
                flash.success("Card deleted");
            }
        } catch (error: any) {
            if (
                error.response?.status === 400 &&
                error?.response?.data?.field?.remoteCard?.reason === "cannot_delete_card"
            ) {
                togglePrompt();
            }
        }
    };

    return (
        <div
            className="flex items-center space-x-11 rounded-md border border-gray-light bg-gray-lighter px-4 py-3"
            data-testid="contact-card-list-item"
        >
            <span>
                <CreditCardIcon name={card.brand} />
                <NoTranslate className="ml-2">
                    *{card.last4} {card.name}
                </NoTranslate>
            </span>
            <NoTranslate className="text-gray-dark" data-testid="contact-card-list-exp-date">
                {card.expMonth}/{card.expYear}
            </NoTranslate>
            <InlineConfirmDeletePopover confirmButtonText="Confirm" onConfirm={async () => handleDeleteCard(card.id)}>
                <SecondaryButton size="small">
                    <TrashIcon />
                </SecondaryButton>
            </InlineConfirmDeletePopover>

            <PromptModal
                title="Delete Card?"
                isOpen={isVisiblePrompt}
                onConfirm={forceDeleteCard}
                onClose={togglePrompt}
            >
                <div className="flex w-full flex-col items-center space-y-2">
                    <Alert>
                        <p>This customer has a booking(s) with an outsanding balance.</p>
                    </Alert>
                    <p>Are you sure you want to delete this card? You will no longer be able to charge this card</p>
                </div>
            </PromptModal>
        </div>
    );
};
